import {getAuth} from 'firebase/auth'

export const AUTH_EXPIRED_EVENT = 'sc-auth-expired'

const AUTH_ERROR_CODES = new Set([
  'unauthenticated',
  'auth/user-token-expired',
  'auth/id-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-credential',
])

export const emitAuthExpired = () => {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
}

const getErrorCode = (error: unknown): string => {
  if (!error || typeof error !== 'object') {
    return ''
  }

  return String((error as {code?: string}).code || '')
}

export const isAuthSessionError = (error: unknown): boolean => {
  const code = getErrorCode(error)
  if (AUTH_ERROR_CODES.has(code)) {
    return true
  }

  const message = error instanceof Error ? error.message : String(error || '')
  return (
    message === 'UNAUTHENTICATED' ||
    message === 'AUTH_TIMEOUT' ||
    /auth\/(id-token-expired|user-token-expired|invalid-user-token)/i.test(message)
  )
}

export const withTimeout = <T,>(promise: Promise<T>, ms = 25000): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error('AUTH_TIMEOUT'))
    }, ms)

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      },
      (error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      }
    )
  })
}

export const refreshFirebaseSession = async (force = false): Promise<string> => {
  const user = getAuth().currentUser
  if (!user) {
    throw new Error('UNAUTHENTICATED')
  }

  return user.getIdToken(force)
}

export const ensureFirebaseSession = async (): Promise<string> => {
  const auth = getAuth()
  await auth.authStateReady()

  const user = auth.currentUser
  if (!user) {
    throw new Error('UNAUTHENTICATED')
  }

  const tokenResult = await user.getIdTokenResult()
  const expiresAt = Date.parse(tokenResult.expirationTime)
  const shouldForceRefresh = !Number.isNaN(expiresAt) && expiresAt - Date.now() < 5 * 60 * 1000

  try {
    return await user.getIdToken(shouldForceRefresh)
  } catch (error) {
    emitAuthExpired()
    throw error
  }
}

export const recoverOrExpireSession = async (error: unknown): Promise<boolean> => {
  if (!isAuthSessionError(error) && getErrorCode(error) !== 'permission-denied') {
    return false
  }

  const user = getAuth().currentUser
  if (!user) {
    emitAuthExpired()
    return true
  }

  try {
    await user.getIdToken(true)
    return false
  } catch {
    emitAuthExpired()
    return true
  }
}

export const runAuthenticatedQuery = async <T,>(fn: () => Promise<T>): Promise<T> => {
  await ensureFirebaseSession()
  try {
    return await withTimeout(fn(), 30000)
  } catch (error) {
    await recoverOrExpireSession(error)
    throw error
  }
}
